import { Fragment, Suspense } from "react";

import { useIntl } from "react-intl";

import { Author } from "@/content/templates/template.types";
import { ContentNotFoundErrorBoundary } from "@/content/utils/content-not-found-error-boundary";
import { expectContent } from "@/content/utils/expectContent";
import { useLocalizedHref } from "@/libraries/web-framework";
import AgileFooter from "@/patterns/agile-footer/src";
import AgileHeading from "@/patterns/agile-heading/src";
import AgilePageLayout from "@/patterns/agile-page-layout/src";
import ArticleShare from "@/patterns/article-share/src";
import GlobalNavigation from "@/patterns/global-nav/src";
import SoftwareDevelopmentProductNav from "@/patterns/product-navs/src/software-development-product-nav";
import { useAgileHubPageTreeData } from "@/templates/agile-hub/src/use-agile-hub-page-tree-data";
import { useLocation } from "@/utils/location-provider";

import { AgileTutorial } from "../tutorial-types";
import { CallToAction } from "../types";

import { useAgileTutorial } from "./agile-tutorial.provider";
import { AgileBreadcrumbs } from "./components/agile-breadcrumbs/src/agile-breadcrumbs";
import AgileIntroduction from "./components/agile-introduction/src";
import { BeforeYouBegin } from "./components/before-you-begin/src/before-you-begin";
import { AgileAuthor } from "./sections/agile-author";
import { RichTextComponent } from "./sections/components/body-text";
import { BodyTextContainer } from "./sections/main-content/body-text-container";
import { NavigationCards } from "./sections/navigation-cards";
import { ShareUpNextBar } from "./sections/share-up-next";

export const AgileTutorialPageTemplate = () => {
    return (
        <ContentNotFoundErrorBoundary>
            <Suspense>
                <AgileTutorialPageTemplateContent />
            </Suspense>
        </ContentNotFoundErrorBoundary>
    );
};

const AgileTutorialPageTemplateContent = () => {
    const agileTutorialData = useAgileTutorial();
    if (!agileTutorialData) {
        return null;
    }

    return <AgileTutorialPageTemplateContentDetails {...agileTutorialData} />;
};

// eslint-disable-next-line complexity
export const AgileTutorialPageTemplateContentDetails = ({
    title,
    subtitle,
    author,
    tutorialBanner,
    nextBestAction,
    tutorialIntroduction,
    ...props
}: AgileTutorial) => {
    const intl = useIntl();
    const location = useLocation();
    const origin = "https://www.atlassian.com";
    const link = `${origin}${location.pathname}`;

    expectContent(title, "No tutorial data found");

    return (
        <>
            <GlobalNavigation
                isShowTryNowLink={true}
                tryNowLinkCtaUrl={useLocalizedHref("/try/cloud/signup")}
                tryNowLinkCtaButtonColor="blue"
                productKey="agile"
            />
            <SoftwareDevelopmentProductNav selectedNavLink="agile" />
            <AgileBreadcrumbs />
            <AgileHeading
                title={title}
                eyebrow={intl.formatMessage({
                    id: "agile.tutorial.heading.eyebrow",
                    defaultMessage: "Tutorial"
                })}
                description={subtitle as string}
                author={author as Author}
                imageUrl={tutorialBanner?.url}
            />
            <AgilePageLayout
                productDiscussedCta={
                    props.productDiscussedCta as CallToAction[]
                }
                relatedTemplateCta={props.relatedTemplateCta as CallToAction[]}
            >
                {props.tutorialSummary && (
                    <RichTextComponent
                        text={props.tutorialSummary}
                        name={null}
                    />
                )}
                {tutorialIntroduction?.length &&
                    tutorialIntroduction.map((data, idx) => (
                        <Fragment key={idx}>
                            <AgileIntroduction
                                heading={data.tutorialIntroductionName}
                                description={data.tutorialIntroductionDetails}
                            />
                        </Fragment>
                    ))}
                {props.beforeYouBegin?.length &&
                    props.beforeYouBegin.map((data, idx) => (
                        <BeforeYouBegin
                            {...data}
                            key={data.beforeYouBeginTitle || idx}
                        />
                    ))}
                {props.tutorialInstructions?.length &&
                    props.tutorialInstructions[0]?.tutorialInstructionSteps?.map(
                        (data, idx) => (
                            <div key={idx}>
                                <BodyTextContainer
                                    name={data.tutorialUseStepTitle ?? ""}
                                    contentComponents={
                                        data.tutorialBodyTextContainer
                                    }
                                />
                            </div>
                        )
                    )}
                <ArticleShare
                    description={props.description}
                    title={title}
                    link={link}
                />
                {author && (
                    <AgileAuthor
                        name={author.name}
                        biography={author.shortBiography}
                        pictureSrc={author.authorPicture?.url}
                        contentType={author.authorPicture?.contentType}
                    />
                )}
            </AgilePageLayout>
            {nextBestAction && nextBestAction.length && (
                <NavigationCards cardsData={nextBestAction} />
            )}
            <ShareUpNextBar
                nodes={useAgileHubPageTreeData()}
                title={title}
                description={props.description}
            />
            <AgileFooter />
        </>
    );
};
